import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AnimatedSplash } from './components/AnimatedSplash';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import IntroPage from './pages/IntroPage';
import SplashPreview from './pages/SplashPreview';
import AuthCallback from './pages/AuthCallback';
import DashboardLayout from './components/DashboardLayout';
import Discover from './pages/Discover';
import ProjectDetail from './pages/ProjectDetail';
import Portfolio from './pages/Portfolio';
import OrbitAI from './pages/OrbitAI';
import Collections from './pages/Collections';
import UploadProject from './pages/UploadProject';
import EditProject from './pages/EditProject';
import Admin from './pages/Admin';
import Pricing from './pages/Pricing';
import Profile from './pages/Profile';
import EditProfile from './pages/EditProfile';
import SecuritySettings from './pages/SecuritySettings';
import PortfolioSetup from './pages/PortfolioSetup';
import PublicPortfolio from './pages/PublicPortfolio';
import ResetPassword from './pages/ResetPassword';
import { LoadingSpinner } from './components/ui/spinner';
import { Capacitor } from '@capacitor/core';
import { usePushNotifications } from './hooks/usePushNotifications';
import { PurchasesProvider } from './contexts/PurchasesContext';

// On native, show the intro page once (first install). Mark it seen in localStorage.
const isNative = Capacitor.isNativePlatform();
const hasSeenIntro = localStorage.getItem('planbase_intro_seen') === 'true';
const shouldShowIntro = isNative && !hasSeenIntro;

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen w-full bg-background flex items-center justify-center">
        <LoadingSpinner
          size="xl"
          message="Loading Planbase..."
          subMessage="Please wait while we set things up"
        />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  const { user, loading } = useAuth();

  // Register for push notifications once user is known (native only)
  usePushNotifications(user?.id);

  // Sync subscription status on load — handled by fetchAndUpdatePlan in AuthContext
  // useSubscription(user?.id);

  return (
    <Routes>
      {/* Intro onboarding — always accessible at /intro; native first-launch redirects here */}
      {/* Dev-only splash preview */}
      <Route path="/splash-preview" element={<SplashPreview />} />

      <Route
        path="/intro"
        element={<IntroPage onDone={() => localStorage.setItem('planbase_intro_seen', 'true')} />}
      />

      <Route 
        path="/login" 
        element={loading ? null : user ? <Navigate to="/" replace /> : <LoginPage />} 
      />
      
      <Route 
        path="/signup" 
        element={loading ? null : user ? <Navigate to="/" replace /> : <SignupPage />} 
      />
      
      <Route 
        path="/auth/callback" 
        element={<AuthCallback />} 
      />

      <Route
        path="/auth/reset-password"
        element={<ResetPassword />}
      />

      {/* Public Portfolio Route - No Auth Required */}
      <Route 
        path="/portfolio/:username" 
        element={<PublicPortfolio />} 
      />
      
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Discover />} />
        <Route path="discover" element={<Discover />} />
        <Route path="project/:id" element={<ProjectDetail />} />
        <Route path="submit-project" element={<UploadProject />} />
        <Route path="my-projects" element={<Navigate to="/portfolio" replace />} />
        <Route path="cms-upload" element={<UploadProject />} />
        <Route path="edit-project/:id" element={<EditProject />} />
        <Route path="portfolio" element={<Portfolio />} />
        <Route path="admin" element={<Admin />} />
        <Route path="pricing" element={<Pricing />} />
        <Route path="orbit-ai" element={<OrbitAI />} />
        <Route path="collections" element={<Collections />} />
        <Route path="profile" element={<Profile />} />
        <Route path="profile/edit" element={<EditProfile />} />
        <Route path="profile/security" element={<SecuritySettings />} />
        <Route path="portfolio-setup" element={<PortfolioSetup />} />
      </Route>

      <Route path="*" element={<Navigate to={shouldShowIntro ? '/intro' : '/'} replace />} />
    </Routes>
  );
}

// Scroll to top on every route change
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <PurchasesProvider>
          <AnimatedSplash />
          <ScrollToTop />
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </PurchasesProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
